import { ProfileChannel } from "@/types/profile";
import { getParentChannels } from "@/utilities/jobs/profileUtils";
import {
  PROFILE_ENVIRONMENT_OPTIONS,
  PROFILE_IMAGES_TO_PUBLISH,
  PROFILE_PUBLISH_DOWNLOAD_CHILD_OPTIONS,
  PROFILE_RELEASE_IMAGE_OPTIONS,
  PROFILE_RELEASE_REQUIRED_STAGES,
  ProfileReleaseRequiredStageName
} from "@/constants/profileConstants";
import {
  formatChannelReleaseMode,
  getChannelDisplayLabel,
  getPublishJobTypeEntries,
  getReleaseAllowedImageSelection,
  getRequiredImageSendEvent,
  getSelectedEnvironments,
  getStageImageSelection
} from "@/utilities/profiles/channelFormUtils";

export const formatReviewValue = (value?: string | number | null): string => {
  if (value === undefined || value === null) return "-";
  if (typeof value === "string" && !value.trim()) return "-";
  return String(value);
};

export const formatReviewBoolean = (
  value: boolean | undefined,
  trueLabel = "Yes",
  falseLabel = "No"
): string => {
  if (value === undefined) return "-";
  return value ? trueLabel : falseLabel;
};

export const formatReviewList = (items?: string[]): string => {
  if (!items?.length) return "-";
  return items.join(", ");
};

export const formatSelectedEnvironments = (channel: ProfileChannel): string => {
  const selected = getSelectedEnvironments(channel);
  if (!selected.length) return "-";
  return selected
    .map((value) => PROFILE_ENVIRONMENT_OPTIONS.find((option) => option.value === value)?.label ?? value)
    .join(", ");
};

/** Lists every image type in the channel's `Publish.JobType` as stored; types without a label show their key. */
export const formatSelectedPublishImages = (channel: ProfileChannel): string => {
  const [installOption, downloadGroup] = PROFILE_IMAGES_TO_PUBLISH;
  const labels = getPublishJobTypeEntries(channel).map(({ jobType, preBuilt }) => {
    const key = jobType.toLowerCase();
    if (key === installOption.jobType) return installOption.label;
    const downloadOption = PROFILE_PUBLISH_DOWNLOAD_CHILD_OPTIONS.find(
      (option) => option.jobType === key && option.preBuilt === preBuilt
    );
    return downloadOption ? `${downloadGroup.label} (${downloadOption.label})` : jobType;
  });
  return formatReviewList(labels);
};

const formatRequiredImageLabels = (channel: ProfileChannel, stage: ProfileReleaseRequiredStageName): string => {
  const selected = getStageImageSelection(channel, stage, "RequiredImages");
  const labels = PROFILE_RELEASE_IMAGE_OPTIONS.filter(({ jobType }) => selected.includes(jobType)).map(
    ({ label }) => label
  );
  return formatReviewList(labels);
};

export const formatReleaseAllowedImages = (channel: ProfileChannel): string => {
  const selected = getReleaseAllowedImageSelection(channel);
  const labels = PROFILE_RELEASE_IMAGE_OPTIONS.filter(({ jobType }) => selected.includes(jobType)).map(
    ({ label }) => label
  );
  return formatReviewList(labels);
};

export const formatStageRequiredImages = (
  channel: ProfileChannel,
  stage: ProfileReleaseRequiredStageName
): string => formatRequiredImageLabels(channel, stage);

export const formatStageSendEvents = (channel: ProfileChannel, stage: ProfileReleaseRequiredStageName): string => {
  const labels = PROFILE_RELEASE_IMAGE_OPTIONS.filter(({ jobType }) => {
    const required = getStageImageSelection(channel, stage, "RequiredImages");
    return required.includes(jobType) && getRequiredImageSendEvent(channel, stage, jobType);
  }).map(({ label }) => label);
  return formatReviewList(labels);
};

export const getReviewChannelSections = (channels: ProfileChannel[]): ProfileChannel[] => getParentChannels(channels);

export const getReviewChannelTitle = (channel: ProfileChannel): string => getChannelDisplayLabel(channel.Name);

export interface ProfileChannelDetailField {
  label: string;
  value?: string;
}

export const buildChannelInfoDetailFields = (channel: ProfileChannel): ProfileChannelDetailField[] => {
  const fields: ProfileChannelDetailField[] = [];

  fields.push(
    { label: "Max Builds", value: formatReviewValue(channel.Release?.MaxBuilds) },
    // { label: "TTL (Time to Live in Days)", value: formatReviewValue(channel.TTL) },
    { label: "Release Mode", value: formatChannelReleaseMode(channel) },
    // { label: "Side by Side", value: formatReviewBoolean(channel.SideBySide, "True", "False") },
    // { label: "Promotion Allowed", value: formatReviewBoolean(channel.PromotionAllowed) },
    { label: "Channel Enabled", value: formatReviewBoolean(channel.Enabled !== false) }
  );

  return fields;
};

export const buildPublishingDetailFields = (channel: ProfileChannel): ProfileChannelDetailField[] => [
  { label: "Environment", value: formatSelectedEnvironments(channel) },
  { label: "Images to Publish", value: formatSelectedPublishImages(channel) }
];

export const buildReleaseDetailFields = (channel: ProfileChannel): ProfileChannelDetailField[] => {
  const fields: ProfileChannelDetailField[] = [
    { label: "Allowed Images", value: formatReleaseAllowedImages(channel) }
  ];

  PROFILE_RELEASE_REQUIRED_STAGES.forEach((stage) => {
    fields.push(
      { label: `${stage}-Required Images`, value: formatStageRequiredImages(channel, stage) },
      { label: `${stage}-Sync Event`, value: formatStageSendEvents(channel, stage) }
    );
  });

  return fields;
};
